import { useState } from "react";
import dayjs from "dayjs";
import { CheckCircle2, ListTodo, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useTeam } from "@/hooks/queries/useTeam";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import { SectionTitle } from "./detail-parts";
import { useContractingTasks } from "@/hooks/queries/useContractingTasks";
import {
  useCreateTask,
  useDeleteTask,
  useSetTaskDone,
} from "@/hooks/mutations/useContractingTaskMutations";
import type { ContractingTask } from "@/types/domain";

function TaskRow({ task }: { task: ContractingTask }) {
  const setDone = useSetTaskDone();
  const remove = useDeleteTask();
  const done = !!task.doneAt;

  return (
    <li className="flex items-start gap-3 border-b border-slate-100 pb-2.5 last:border-0 last:pb-0 dark:border-slate-800">
      <button
        type="button"
        title={done ? "Marcar pendiente" : "Marcar hecha"}
        disabled={setDone.isPending}
        onClick={() =>
          setDone.mutate(
            { id: task.id, done: !done, contracting_id: task.contracting.id },
            { onError: () => toast.error("No se pudo actualizar la tarea") },
          )
        }
        className={cn(
          "mt-0.5 shrink-0 cursor-pointer transition-colors",
          done
            ? "text-emerald-600"
            : "text-slate-300 hover:text-emerald-600 dark:text-slate-600",
        )}
      >
        <CheckCircle2 className="size-5" />
      </button>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "break-words text-sm font-medium",
            done && "text-slate-400 line-through",
          )}
        >
          {task.title}
        </p>
        <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
          <span>
            {done
              ? `hecha el ${dayjs(task.doneAt).format("DD MMM")}`
              : `anotada el ${dayjs(task.createdAt).format("DD MMM")}`}
          </span>
          {task.assignee && <span>· {task.assignee.name}</span>}
        </p>
      </div>

      {task.assignee && (
        <MemberAvatar member={task.assignee} className="mt-0.5 size-6 shrink-0" />
      )}

      <button
        type="button"
        title="Borrar tarea"
        disabled={remove.isPending}
        onClick={() =>
          remove.mutate(
            { id: task.id, contracting_id: task.contracting.id },
            { onError: () => toast.error("No se pudo borrar la tarea") },
          )
        }
        className="mt-0.5 shrink-0 cursor-pointer text-slate-300 transition-colors hover:text-red-500 dark:text-slate-600"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}

/**
 * Los compromisos del equipo sobre esta licitación. Es lo único del CRM que nace
 * del comercial y no del cronograma del SEACE. Sin plazo: la columna sigue en la
 * base, pero hoy no ordena ni dispara nada.
 */
export function TasksCard({ contractingId }: { contractingId: number }) {
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const { data: team } = useTeam();
  const { data: tasks, isLoading } = useContractingTasks({
    contractingId,
    includeDone: true,
  });
  const create = useCreateTask();

  const activeMembers = team?.members.filter((m) => m.isActive) ?? [];

  function add() {
    const text = title.trim();
    if (!text) return;
    create.mutate(
      {
        contracting_id: contractingId,
        title: text,
        assigned_user_id: assignee || undefined,
      },
      {
        onSuccess: () => {
          setTitle("");
          setAssignee("");
        },
        onError: () => toast.error("No se pudo crear la tarea"),
      },
    );
  }

  const pending = tasks?.filter((t) => !t.doneAt) ?? [];
  const done = tasks?.filter((t) => t.doneAt) ?? [];

  return (
    <div className="flex flex-col">
      <SectionTitle icon={<ListTodo className="size-4 text-violet-500" />}>
        Tareas
      </SectionTitle>
      <div className="mt-3 flex flex-col rounded-2xl border bg-card p-5">
        <div className="flex flex-col gap-2">
          {/* Botón en la misma fila que el campo: la tarjeta se come menos alto. */}
          <div className="flex items-center gap-2">
            <Input
              id={`task-title-${contractingId}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Ej. Llamar al residente de obra"
              className="min-w-0 flex-1 text-sm"
            />
            <Button
              size="sm"
              onClick={add}
              disabled={create.isPending || !title.trim()}
              className="shrink-0 cursor-pointer"
            >
              {create.isPending ? "Creando..." : "Agregar"}
            </Button>
          </div>
          {activeMembers.length > 1 && (
            <select
              id={`task-assignee-${contractingId}`}
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="h-9 cursor-pointer self-start rounded-md border bg-transparent px-2 text-sm"
            >
              <option value="">Para mí</option>
              {activeMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mt-2">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : !tasks?.length ? (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Sin tareas. Anota el próximo paso y no se te pasa.
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {[...pending, ...done].map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
